import { useEffect, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { EVENT_TYPE_APPOINTMENT } from "constants/appointments";
import AlertDialog from "features/patients/sections/patient-alerts/alert-dialog.component";
import useAppointments, {
  AppointmentsProvider
} from "features/scheduling/hooks/use-appointments.hook";
import { openForm } from "features/scheduling/scheduler-slice";
import { useStickyTrue } from "hooks/use-sticky-true.hook";
import type { Appointment } from "services/types/appointments.type";

import QuickLauncherHelpDialog from "./quick-launcher-help-dialog.component";
import {
  dismissAlertRequest,
  selectQuickLauncherAlertRequest,
  selectQuickLauncherAppointmentRequest
} from "./quick-launcher.slice";
import QuickLauncherScreenshotDialog from "./screenshot/screenshot-dialog.component";

/**
 * Opens the appointment form for the current request. Lives *inside* the
 * launcher's own `AppointmentsProvider` because `appointmentFormId` is minted
 * per provider instance — the same reason the dialer's active-call widget and
 * the patient drawer each mount their own. The form only renders while
 * `openFormId === formId`, so a chart's or the scheduler's provider being
 * mounted alongside is harmless.
 */
function QuickLauncherAppointmentOpener() {
  const dispatch = useAppDispatch();
  const request = useAppSelector(selectQuickLauncherAppointmentRequest);
  const { setupAppointmentModal, appointmentFormId } = useAppointments();

  // The provider's own mount effect dispatches `closeForm()` (its
  // `editingForm` sync in `use-appointments.hook.tsx`), and React runs a
  // child's effects *before* its parent's — so an `openForm` from this
  // component's mount effect is undone a moment later, and the very first
  // request appeared to do nothing. Flipping state in a mount effect and
  // opening from the re-render that follows puts the open after the whole
  // mount commit, parent effects included, without a timer.
  const [isProviderMounted, setIsProviderMounted] = useState(false);
  useEffect(() => {
    setIsProviderMounted(true);
  }, []);

  useEffect(() => {
    if (!isProviderMounted || !request) return;
    setupAppointmentModal(
      {
        type: EVENT_TYPE_APPOINTMENT,
        ...(request.patient ? { patient: request.patient } : {})
      } as Partial<Appointment>,
      "add"
    );
    dispatch(openForm(appointmentFormId));
    // Keyed on the nonce (plus the one-shot mount flag). `setupAppointmentModal`'s
    // identity changes with the provider's own state, and re-running on that
    // would reopen a form the user has just closed (the SN-17825 shape of bug).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProviderMounted, request?.nonce]);

  return null;
}

/**
 * The dialogs the quick launcher can open from anywhere, rendered as siblings
 * of the launcher's own Dialog so they survive it closing.
 *
 * The appointment provider is mounted on first use and then kept (sticky):
 * `formOnly` means it never fetches events, so keeping it costs one uuid and a
 * cached clinics query, while remounting per request would drop MUI's close
 * transition on the form. The alert, screenshot and help dialogs are cheap and
 * always mounted, closed.
 */
export default function QuickLauncherHosts() {
  const dispatch = useAppDispatch();
  const appointmentRequest = useAppSelector(
    selectQuickLauncherAppointmentRequest
  );
  const alertRequest = useAppSelector(selectQuickLauncherAlertRequest);
  const shouldMountAppointments = useStickyTrue(Boolean(appointmentRequest));

  return (
    <>
      {shouldMountAppointments && (
        <AppointmentsProvider formOnly>
          <QuickLauncherAppointmentOpener />
        </AppointmentsProvider>
      )}
      <AlertDialog
        open={Boolean(alertRequest)}
        onClose={() => dispatch(dismissAlertRequest())}
        patientId={alertRequest?.patientId}
        patientName={alertRequest?.patientName}
        // The add mutation invalidates the `PatientAlerts` tag, which is what
        // the header badge and the alerts tab read; nothing here to refetch.
        refetch={() => undefined}
      />
      <QuickLauncherScreenshotDialog />
      <QuickLauncherHelpDialog />
    </>
  );
}
